import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, TextField, Button, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, DialogContentText } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

const InterviewTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateQuestions, setNewTemplateQuestions] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [templateToDelete, setTemplateToDelete] = useState(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/templates`);
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const handleAddTemplate = async () => {
        try {
            const template = {
                name: newTemplateName,
                questions: newTemplateQuestions.split('\n')
            };
            await axios.post(`${process.env.REACT_APP_API_URL}/api/templates`, template);
            setNewTemplateName('');
            setNewTemplateQuestions('');
            fetchTemplates();
        } catch (error) {
            console.error('Error adding template:', error);
        }
    };

    const handleDeleteTemplate = async () => {
        try {
            if (templateToDelete) {
                await axios.delete(`${process.env.REACT_APP_API_URL}/api/templates/${templateToDelete}`);
            } else {
                for (const id of selectedTemplates) {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/api/templates/${id}`);
                }
            }
            fetchTemplates();
            setSelectedTemplates([]);
            setTemplateToDelete(null);
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting template:', error);
        }
    };

    const handleTemplateClick = (template) => {
        setCurrentTemplate(template);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentTemplate(null);
    };

    const handleSelectTemplate = (id) => {
        setSelectedTemplates(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleDeleteIconClick = (id) => {
        setTemplateToDelete(id);
        setDeleteDialogOpen(true);
    };

    return (
        <Container>
            <Typography variant="h4" gutterBottom>Interview Templates</Typography>
            <Paper sx={{ p: 3, mb: 3 }}>
                <TextField
                    label="Template Name"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Questions (one per line)"
                    value={newTemplateQuestions}
                    onChange={(e) => setNewTemplateQuestions(e.target.value)}
                    fullWidth
                    multiline
                    rows={10}
                    margin="normal"
                />
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddTemplate}>
                    Add Template
                </Button>
            </Paper>
            <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={selectedTemplates.length === 0}
                sx={{ marginBottom: 2 }}
            >
                Delete Selected
            </Button>
            <List>
                {templates.map((template) => (
                    <ListItem key={template.id} button onClick={() => handleTemplateClick(template)}>
                        <Checkbox
                            checked={selectedTemplates.includes(template.id)}
                            onChange={() => handleSelectTemplate(template.id)}
                            tabIndex={-1}
                            disableRipple
                        />
                        <ListItemText primary={template.name} />
                        <IconButton edge="end" aria-label="delete" onClick={(event) => {event.stopPropagation(); handleDeleteIconClick(template.id)}}>
                            <DeleteIcon />
                        </IconButton>
                    </ListItem>
                ))}
            </List>
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle>{currentTemplate?.name}</DialogTitle>
                <DialogContent>
                    <List>
                        {currentTemplate?.questions.map((question, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={question} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Interview Templates</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected template(s)? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteTemplate} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default InterviewTemplates;
